import { useCallback, useEffect, useState } from 'react'
import { useIntercom } from 'react-use-intercom'

import { useLazyGetIntercomTokenQuery } from '@/app/services/integrations'

import { getLanguageToUse } from '../utils/GeneralUtils/GeneralUtils'

import { useToolBoxTreatments } from './useToolBoxTreatments'

/**
 * Build the payload object passed to `react-use-intercom`'s `boot()`.
 *
 * QM-3437: organisation/company data is now signed into the backend Intercom
 * JWT, so the payload intentionally carries NO `company` block — Intercom
 * ignores unsigned company data once Messenger JWT enforcement is on, and the
 * JWT is the single source of truth for company association.
 *
 * @param {Object} params
 * @param {string} params.token - Intercom JWT (carries the signed company claims)
 * @param {Object} params.organisation - Active organisation (used for the language override)
 * @param {Object} params.userContext - User context with .user
 * @returns {Object} The boot payload
 */
export const buildIntercomBootPayload = ({ organisation, token, userContext }) => {
    return {
        intercomUserJwt: token,
        languageOverride: getLanguageToUse(userContext.user, organisation),
        customAttributes: {
            device_width: window.screen.width,
            device_height: window.screen.height,
            user_agent: window.navigator.userAgent,
        },
    }
}

export const useIntercomMessenger = () => {
    const { boot, shutdown } = useIntercom()

    const [bootSuccess, setBootSuccess] = useState(false)

    const [getIntercomToken] = useLazyGetIntercomTokenQuery()
    const { showIntercom } = useToolBoxTreatments()

    const bootIntercomMessenger = useCallback(
        async (organisation, userContext) => {
            setBootSuccess(false)
            if (!organisation || !userContext) {
                console.debug('[IntercomInitialization] Missing organization or user data, skipping Intercom boot')
                return
            }

            console.debug('[IntercomInitialization] Booting Intercom Messenger')

            try {
                console.debug('[IntercomInitialization] Starting Intercom')
                const intercomToken = await getIntercomToken().unwrap()

                if (!intercomToken?.token) {
                    console.error('[IntercomInitialization] Failed to retrieve Intercom token')
                    return
                }

                boot(
                    buildIntercomBootPayload({
                        token: intercomToken.token,
                        organisation,
                        userContext,
                    })
                )
                console.debug('[IntercomInitialization] Intercom booted successfully')
                setBootSuccess(true)
            } catch (error) {
                console.error('[IntercomInitialization] Error booting Intercom:', error)
            }
        },
        [boot, getIntercomToken]
    )

    useEffect(() => {
        if (bootSuccess && !showIntercom) {
            shutdown()
        }
    }, [showIntercom, bootSuccess, shutdown])

    useEffect(() => {
        return () => {
            shutdown()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return {
        bootIntercomMessenger,
        shutdown,
    }
}
